// Stillhouse view — Mesh & access. Netbird mesh status + mode (OPEN/RESTRICTED),
// peer inventory, and per-machine user assignment (drives RESTRICTED policies).
// Viewing needs fleet.read; the mode toggle needs mesh.manage and assignment
// changes need fleet.manage (matching the API route guards).
import React, { useCallback, useEffect, useState } from "react";
import { C, F, Btn, Pill } from "../brand/apo";
import { Card, StatusDot, Chip, Avatar, PageHeader, Stat, COPPER } from "../brand/sh";
import { Modal } from "../components/Modal";
import { api, STATUS_KEY, type AccessPair, type AccessRule, type ClientDevice, type FleetMachine, type FleetUser, type MeshMode, type MeshPeer, type MeshStatus } from "../api";
import { useAuth } from "../auth";

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, overflow: "auto", padding: "22px 32px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
);

function fmtLastSeen(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// Map a device's netbird peer state onto the design system's status dots.
// The API vocabulary is "pending" | "active" | "removing" | null
// (apps/api/src/routes/clientDevices.ts peerState()).
function peerStateDot(state: string | null): { key: string; label: string } {
  if (!state) return { key: "offline", label: "No peer" };
  if (state === "pending") return { key: "pending", label: "Provisional" };
  if (state === "removing") return { key: "error", label: "Removing" };
  if (state === "active") return { key: "online", label: "Joined" };
  return { key: "offline", label: state };
}

export function MeshAccess() {
  const { can } = useAuth();
  const canManageMesh = can("mesh.manage");
  const canAssign = can("fleet.manage");

  const [status, setStatus] = useState<MeshStatus | null>(null);
  const [peers, setPeers] = useState<MeshPeer[]>([]);
  const [machines, setMachines] = useState<FleetMachine[]>([]);
  const [users, setUsers] = useState<FleetUser[]>([]);
  const [devices, setDevices] = useState<ClientDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmMode, setConfirmMode] = useState<MeshMode | null>(null);
  const [switching, setSwitching] = useState(false);
  const [savingMachineId, setSavingMachineId] = useState<string | null>(null);
  const [syncPending, setSyncPending] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ClientDevice | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [rules, setRules] = useState<AccessRule[]>([]);
  const [pairs, setPairs] = useState<AccessPair[]>([]);
  const [newEffect, setNewEffect] = useState<"ALLOW" | "DENY">("ALLOW");
  const [newDevice, setNewDevice] = useState("");
  const [newHost, setNewHost] = useState("");
  const [newNote, setNewNote] = useState("");
  const [savingRule, setSavingRule] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, p, m, u, d, ar, ae] = await Promise.all([
        api.getMeshStatus(),
        api.getMeshPeers(),
        api.getMachines(),
        api.getFleetUsers(),
        api.getClientDevices(),
        api.getAccessRules(),
        api.getAccessEffective(),
      ]);
      setRules(ar.rules);
      setPairs(ae.pairs);
      setStatus(s);
      setPeers(p.peers);
      setMachines(m.machines);
      setUsers(u.users);
      setDevices(d.devices);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const mode: MeshMode = status?.mode ?? "OPEN";
  const modeColor = mode === "RESTRICTED" ? COPPER : C.glow;

  const switchMode = async (next: MeshMode) => {
    setSwitching(true);
    setError(null);
    try {
      const r = await api.setMeshMode(next);
      setSyncPending(r.meshSync === "pending");
      setConfirmMode(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSwitching(false);
    }
  };

  const assign = async (machineId: string, userId: string | null) => {
    setSavingMachineId(machineId);
    setError(null);
    try {
      const r = await api.assignMachine(machineId, userId);
      setSyncPending(r.meshSync === "pending");
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingMachineId(null);
    }
  };

  // Device -> host access rules. Each change re-syncs NetBird server-side.
  const runRuleAction = async (action: () => Promise<{ meshSync?: string }>) => {
    setSavingRule(true);
    setError(null);
    try {
      const r = await action();
      setSyncPending(r.meshSync === "pending");
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingRule(false);
    }
  };
  const addRule = () =>
    runRuleAction(async () => {
      const r = await api.createAccessRule({
        effect: newEffect,
        deviceId: newDevice || null,
        machineId: newHost || null,
        note: newNote.trim() || undefined,
      });
      setNewNote("");
      return r;
    });

  // Revoke a client device: optimistic removal, then DELETE + refresh. The API
  // tears the mesh peer down with the same tombstone-retry semantics hosts use.
  const revoke = async (device: ClientDevice) => {
    if (revoking) return;
    setRevoking(true);
    setError(null);
    setDevices((prev) => prev.filter((d) => d.id !== device.id));
    try {
      await api.deleteClientDevice(device.id);
      setRevokeTarget(null);
      await load();
    } catch (err) {
      // Refresh first (load() clears the error), then surface the failure so
      // the reappearing device comes with a visible explanation.
      setRevokeTarget(null);
      await load();
      setError((err as Error).message);
    } finally {
      setRevoking(false);
    }
  };

  return (
    <React.Fragment>
      <PageHeader
        crumb="GOVERNANCE › MESH"
        title="Mesh & access"
        sub="The Netbird overlay every host and client rides. OPEN lets any enrolled client reach any host; RESTRICTED narrows each user to the machines assigned to them."
      >
        <Pill color={modeColor} glow={mode === "RESTRICTED"}>{mode}</Pill>
        <Btn ghost onClick={() => void load()}>
          Refresh
        </Btn>
      </PageHeader>
      <Scroll>
        {error && (
          <div style={{ fontFamily: F.mono, fontSize: 11, color: C.rose, letterSpacing: "0.04em" }}>{error}</div>
        )}
        {syncPending && (
          <div style={{ fontFamily: F.mono, fontSize: 11, color: C.amber, letterSpacing: "0.04em" }}>
            Mesh policy sync pending — the reconciler retries until NetBird converges.
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
          <Stat label="Mode" value={mode} accent={modeColor} sub={status?.configured ? "mesh configured" : "mesh not configured yet"} />
          <Stat label="Host peers" value={String(status?.peers.hosts ?? 0)} accent={C.glow} />
          <Stat label="Client peers" value={String(status?.peers.clients ?? 0)} accent={C.lilac} />
          <Stat label="Provisional" value={String(status?.peers.pending ?? 0)} sub="enroll key minted · not joined" accent={C.amber} />
        </div>

        <Card
          title="Access mode"
          tag={<Chip color={modeColor}>{mode}</Chip>}
          action={
            canManageMesh ? (
              <div style={{ display: "flex", gap: 8 }}>
                <Btn
                  ghost={mode !== "OPEN"}
                  primary={mode === "OPEN"}
                  size="md"
                  style={{ padding: "5px 14px", fontSize: 12 }}
                  onClick={() => mode !== "OPEN" && setConfirmMode("OPEN")}
                >
                  Open
                </Btn>
                <Btn
                  ghost={mode !== "RESTRICTED"}
                  primary={mode === "RESTRICTED"}
                  size="md"
                  style={{ padding: "5px 14px", fontSize: 12 }}
                  onClick={() => mode !== "RESTRICTED" && setConfirmMode("RESTRICTED")}
                >
                  Restricted
                </Btn>
              </div>
            ) : (
              <span style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: "0.14em", color: C.fgFaint }}>VIEW ONLY</span>
            )
          }
        >
          <div style={{ fontSize: 13, color: C.fgMute, lineHeight: 1.6 }}>
            <span style={{ color: C.glow, fontFamily: F.mono, fontSize: 11 }}>OPEN</span> — every enrolled client can reach every host on the mesh.
            <br />
            <span style={{ color: COPPER, fontFamily: F.mono, fontSize: 11 }}>RESTRICTED</span> — clients only reach the machines assigned to their user below.
          </div>
          <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10, fontFamily: F.mono, fontSize: 11, color: C.fgFaint, letterSpacing: "0.04em" }}>
            <span style={{ letterSpacing: "0.16em", fontWeight: 600 }}>MGMT</span>
            <span style={{ color: status?.managementUrl ? C.fgMute : C.fgFaint }}>{status?.managementUrl ?? "not configured"}</span>
          </div>
        </Card>

        <Card title="Mesh peers" tag={<Chip color={C.lilac}>{peers.length} total</Chip>}>
          {peers.length === 0 ? (
            <div style={{ color: C.fgMute, fontSize: 13, padding: "10px 0" }}>
              No peers yet. Mint an enrollment token from <span style={{ color: C.glow }}>Machines & pools</span> — hosts and clients appear here when they join.
            </div>
          ) : (
            <div style={{ margin: "-16px -18px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "0.7fr 1.3fr 1fr 1.2fr 0.9fr 0.9fr", gap: 14, padding: "11px 18px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.16em", color: C.fgFaint, fontWeight: 600, borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
                <span>KIND</span>
                <span>PEER</span>
                <span>MESH IP</span>
                <span>USER</span>
                <span>LAST SEEN</span>
                <span>STATE</span>
              </div>
              {peers.map((p, i) => (
                <div key={p.id} style={{ display: "grid", gridTemplateColumns: "0.7fr 1.3fr 1fr 1.2fr 0.9fr 0.9fr", gap: 14, padding: "13px 18px", borderBottom: i < peers.length - 1 ? `1px solid ${C.line}` : "none", alignItems: "center", fontFamily: F.ui, fontSize: 13 }}>
                  <span>
                    <Chip color={p.kind === "HOST" ? C.glow : C.lilac}>{p.kind}</Chip>
                  </span>
                  <span style={{ fontFamily: F.mono, fontWeight: 700, letterSpacing: "0.03em", color: C.fgDark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.machine?.hostname ?? p.device?.name ?? p.peerId}
                  </span>
                  <span style={{ fontFamily: F.mono, fontSize: 11.5, color: C.fgMute }}>{p.ip ?? "—"}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, color: C.fgDark, minWidth: 0 }}>
                    {p.user ? (
                      <React.Fragment>
                        <Avatar name={p.user.displayName} size={24} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.user.displayName}</span>
                      </React.Fragment>
                    ) : (
                      <span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint }}>—</span>
                    )}
                  </span>
                  <span style={{ fontFamily: F.mono, fontSize: 11.5, color: C.fgMute }}>{fmtLastSeen(p.lastSeen)}</span>
                  <StatusDot status={p.pending ? "pending" : "online"} label={p.pending ? "Provisional" : "Joined"} />
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card
          title="Machine assignment"
          tag={<Chip color={COPPER}>drives RESTRICTED</Chip>}
          action={
            !canAssign ? (
              <span style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: "0.14em", color: C.fgFaint }}>VIEW ONLY</span>
            ) : undefined
          }
        >
          <div style={{ fontSize: 13, color: C.fgMute, lineHeight: 1.55, marginBottom: 4 }}>
            In RESTRICTED mode each user only reaches the machines assigned here. Unassigned machines are unreachable from client devices.
          </div>
          <div style={{ margin: "12px -18px -16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.9fr 1.2fr 1.2fr", gap: 14, padding: "11px 18px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.16em", color: C.fgFaint, fontWeight: 600, borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
              <span>MACHINE</span>
              <span>STATUS</span>
              <span>ASSIGNED TO</span>
              <span>{canAssign ? "REASSIGN" : ""}</span>
            </div>
            {machines.length === 0 && (
              <div style={{ padding: "22px 18px", color: C.fgMute, fontSize: 13 }}>No machines enrolled yet.</div>
            )}
            {machines.map((m, i) => (
              <div key={m.id} style={{ display: "grid", gridTemplateColumns: "1.2fr 0.9fr 1.2fr 1.2fr", gap: 14, padding: "12px 18px", borderBottom: i < machines.length - 1 ? `1px solid ${C.line}` : "none", alignItems: "center", fontFamily: F.ui, fontSize: 13 }}>
                <span style={{ fontFamily: F.mono, fontWeight: 700, letterSpacing: "0.03em", color: C.fgDark }}>{m.hostname}</span>
                <StatusDot status={STATUS_KEY[m.status]} />
                <span style={{ display: "flex", alignItems: "center", gap: 8, color: C.fgDark }}>
                  {m.assignedTo ? (
                    <React.Fragment>
                      <Avatar name={m.assignedTo} size={24} />
                      {m.assignedTo}
                    </React.Fragment>
                  ) : (
                    <span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint }}>unassigned</span>
                  )}
                </span>
                {canAssign ? (
                  <select
                    style={{ width: "100%", background: C.surfaceHi, border: `1px solid ${C.line}`, color: C.fgDark, fontFamily: F.ui, fontSize: 12.5, padding: "7px 10px", appearance: "auto", opacity: savingMachineId === m.id ? 0.5 : 1 }}
                    value={m.assignedToId ?? ""}
                    disabled={savingMachineId === m.id}
                    onChange={(e) => {
                      const v = e.target.value;
                      void assign(m.id, v === "" ? null : v);
                    }}
                  >
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.displayName}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
          {canAssign && users.length === 0 && (
            <div style={{ marginTop: 14, fontFamily: F.mono, fontSize: 10.5, color: C.fgFaint, lineHeight: 1.6 }}>
              No assignable users yet — invite people from Teams &amp; users and they appear here.
            </div>
          )}
        </Card>

        <Card
          title="Device access rules"
          tag={<Chip color={COPPER}>deny wins</Chip>}
          action={
            !canManageMesh ? (
              <span style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: "0.14em", color: C.fgFaint }}>VIEW ONLY</span>
            ) : undefined
          }
        >
          <div style={{ fontSize: 13, color: C.fgMute, lineHeight: 1.55, marginBottom: 4 }}>
            Which client devices may reach which hosts. A pair is allowed when an ALLOW rule matches and no DENY rule does; with no matching rule it is denied.
            {mode !== "RESTRICTED" && (
              <span style={{ color: C.amber }}> Rules are only enforced in RESTRICTED mode - the mesh is currently OPEN.</span>
            )}
          </div>
          {canManageMesh && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "12px 0" }}>
              <select style={{ background: C.surfaceHi, border: `1px solid ${C.line}`, color: C.fgDark, fontFamily: F.ui, fontSize: 12.5, padding: "7px 10px" }} value={newEffect} onChange={(e) => setNewEffect(e.target.value as "ALLOW" | "DENY")}>
                <option value="ALLOW">Allow</option>
                <option value="DENY">Deny</option>
              </select>
              <select style={{ background: C.surfaceHi, border: `1px solid ${C.line}`, color: C.fgDark, fontFamily: F.ui, fontSize: 12.5, padding: "7px 10px" }} value={newDevice} onChange={(e) => setNewDevice(e.target.value)}>
                <option value="">Any device</option>
                {devices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.id.slice(-4)})
                  </option>
                ))}
              </select>
              <span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint }}>to</span>
              <select style={{ background: C.surfaceHi, border: `1px solid ${C.line}`, color: C.fgDark, fontFamily: F.ui, fontSize: 12.5, padding: "7px 10px" }} value={newHost} onChange={(e) => setNewHost(e.target.value)}>
                <option value="">Any host</option>
                {machines.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.hostname}
                  </option>
                ))}
              </select>
              <input
                style={{ background: C.surfaceHi, border: `1px solid ${C.line}`, color: C.fgDark, fontFamily: F.ui, fontSize: 12.5, padding: "7px 10px" }}
                placeholder="Note (optional)"
                maxLength={200}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <Btn primary style={{ padding: "6px 16px", fontSize: 12, opacity: savingRule ? 0.7 : 1 }} onClick={() => !savingRule && void addRule()}>
                Add rule
              </Btn>
            </div>
          )}
          <div style={{ margin: "12px -18px 0" }}>
            {rules.length === 0 && (
              <div style={{ padding: "14px 18px", color: C.fgMute, fontSize: 13 }}>No rules yet - no device can reach any host in RESTRICTED mode.</div>
            )}
            {rules.map((r, i) => (
              <div
                key={r.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "0.6fr 1.3fr 1.3fr 1.4fr 1fr",
                  gap: 14,
                  padding: "11px 18px",
                  borderTop: i === 0 ? `1px solid ${C.line}` : undefined,
                  borderBottom: `1px solid ${C.line}`,
                  alignItems: "center",
                  opacity: r.enabled ? 1 : 0.5,
                }}
              >
                <span>
                  <Chip color={r.effect === "ALLOW" ? C.glow : C.rose}>{r.effect}</Chip>
                </span>
                <span style={{ fontFamily: F.mono, fontSize: 12, color: C.fgDark }}>
                  {r.deviceId ? `${r.deviceName ?? r.deviceId} (${r.deviceId.slice(-4)})` : "any device"}
                </span>
                <span style={{ fontFamily: F.mono, fontSize: 12, color: C.fgDark }}>{r.machineId ? (r.hostname ?? r.machineId) : "any host"}</span>
                <span style={{ fontSize: 12, color: C.fgMute, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.note ?? ""}</span>
                {canManageMesh ? (
                  <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <Btn ghost style={{ padding: "4px 10px", fontSize: 11.5 }} onClick={() => void runRuleAction(() => api.setAccessRuleEnabled(r.id, !r.enabled))}>
                      {r.enabled ? "Disable" : "Enable"}
                    </Btn>
                    <Btn ghost style={{ padding: "4px 10px", fontSize: 11.5, color: C.rose, borderColor: `${C.rose}55` }} onClick={() => void runRuleAction(() => api.deleteAccessRule(r.id))}>
                      Delete
                    </Btn>
                  </span>
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
          {pairs.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: "0.16em", color: C.fgFaint, marginBottom: 8 }}>EFFECTIVE ACCESS</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {pairs.map((p) => (
                  <div key={`${p.deviceId}:${p.machineId}`} style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: F.mono, fontSize: 11.5, color: C.fgMute }}>
                    <Chip color={p.allowed ? C.glow : C.rose}>{p.allowed ? "ALLOWED" : "DENIED"}</Chip>
                    <span style={{ color: C.fgDark }}>
                      {p.deviceName ?? p.deviceId} ({p.deviceId.slice(-4)}) to {p.hostname ?? p.machineId}
                    </span>
                    <span style={{ color: C.fgFaint }}>{p.reason === "denied-by-rule" ? "deny rule" : p.reason === "allowed" ? "allow rule" : "no matching allow"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card title="Client devices" tag={<Chip color={C.lilac}>{devices.length} enrolled</Chip>}>
          {devices.length === 0 ? (
            <div style={{ color: C.fgMute, fontSize: 13, padding: "10px 0" }}>
              No client devices enrolled yet. Mint a <span style={{ color: C.lilac }}>CLIENT</span> token from{" "}
              <span style={{ color: C.glow }}>Machines &amp; pools</span> — devices appear here when they burn it.
            </div>
          ) : (
            <div style={{ margin: "-16px -18px" }}>
              <div style={{ display: "grid", gridTemplateColumns: canAssign ? "1.3fr 1.2fr 0.7fr 1fr 0.9fr 0.9fr 0.7fr" : "1.3fr 1.2fr 0.7fr 1fr 0.9fr 0.9fr", gap: 14, padding: "11px 18px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.16em", color: C.fgFaint, fontWeight: 600, borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
                <span>DEVICE</span>
                <span>USER</span>
                <span>PLATFORM</span>
                <span>MESH IP</span>
                <span>LAST SEEN</span>
                <span>STATE</span>
                {canAssign && <span />}
              </div>
              {devices.map((d, i) => {
                const dot = peerStateDot(d.peerState);
                return (
                  <div key={d.id} style={{ display: "grid", gridTemplateColumns: canAssign ? "1.3fr 1.2fr 0.7fr 1fr 0.9fr 0.9fr 0.7fr" : "1.3fr 1.2fr 0.7fr 1fr 0.9fr 0.9fr", gap: 14, padding: "12px 18px", borderBottom: i < devices.length - 1 ? `1px solid ${C.line}` : "none", alignItems: "center", fontFamily: F.ui, fontSize: 13 }}>
                    <span style={{ fontFamily: F.mono, fontWeight: 700, letterSpacing: "0.03em", color: C.fgDark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {d.name}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 8, color: C.fgDark, minWidth: 0 }}>
                      {d.userName ? (
                        <React.Fragment>
                          <Avatar name={d.userName} size={24} />
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.userName}</span>
                        </React.Fragment>
                      ) : (
                        <span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint }}>unbound</span>
                      )}
                    </span>
                    <span style={{ fontFamily: F.mono, fontSize: 11.5, color: C.fgMute, textTransform: "capitalize" }}>{d.platform ?? "—"}</span>
                    <span style={{ fontFamily: F.mono, fontSize: 11.5, color: C.fgMute }}>{d.meshIp ?? "—"}</span>
                    <span style={{ fontFamily: F.mono, fontSize: 11.5, color: C.fgMute }}>{fmtLastSeen(d.lastSeenAt)}</span>
                    <StatusDot status={dot.key} label={dot.label} />
                    {canAssign && (
                      <span style={{ textAlign: "right" }}>
                        <Btn ghost style={{ padding: "5px 12px", fontSize: 12, color: C.rose, borderColor: `${C.rose}55` }} onClick={() => setRevokeTarget(d)}>
                          Revoke
                        </Btn>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </Scroll>

      <Modal
        open={confirmMode !== null}
        onClose={() => !switching && setConfirmMode(null)}
        title={`Switch mesh to ${confirmMode ?? ""}?`}
        sub={
          confirmMode === "RESTRICTED"
            ? "Clients will only reach the machines assigned to their user. Anyone without an assignment loses host connectivity the moment policies sync."
            : "Every enrolled client will be able to reach every host on the mesh. Per-user assignments stop limiting connectivity until you switch back."
        }
        width={480}
      >
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Btn ghost onClick={() => setConfirmMode(null)}>
            Cancel
          </Btn>
          <Btn primary onClick={() => confirmMode && void switchMode(confirmMode)}>
            {switching ? "Switching…" : `Switch to ${confirmMode ?? ""}`}
          </Btn>
        </div>
      </Modal>

      <Modal
        open={revokeTarget !== null}
        onClose={() => !revoking && setRevokeTarget(null)}
        title={`Revoke ${revokeTarget?.name ?? "device"}?`}
        sub="The device is removed from the org and torn out of the Netbird mesh. It loses host connectivity the moment the peer is revoked and must re-enroll with a fresh token to come back."
        width={480}
      >
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Btn ghost onClick={() => !revoking && setRevokeTarget(null)}>
            Cancel
          </Btn>
          <Btn
            primary
            style={{ background: C.rose, borderColor: C.rose, opacity: revoking ? 0.7 : 1, pointerEvents: revoking ? "none" : "auto" }}
            onClick={() => revokeTarget && void revoke(revokeTarget)}
          >
            {revoking ? "Revoking…" : "Revoke device"}
          </Btn>
        </div>
      </Modal>
    </React.Fragment>
  );
}
